import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import MasterModalShell from "./common/MasterModalShell";
import CodeField from "./common/CodeField";
import { validateCode, validateRequired } from "./common/validation";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import { riskCohortApi } from "api/immunization";
import {
  COHORT_RULE_LOGIC,
  COHORT_RULE_OPERATORS,
  COHORT_RULE_SOURCES,
  FIELD_BEARING_SOURCES,
  humanise,
} from "config/immunizationConstants";

const emptyRule = () => ({
  sourceType: "CHRONIC_DISEASE",
  field: "",
  operator: "EQUALS",
  value: "",
  valueTo: "",
  notes: "",
});

const RiskCohortModal = ({ open, onClose, data }) => {
  const cohortId = data?._id;
  const isUpdate = Boolean(cohortId);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      code: data?.code || "",
      cohortName: data?.cohortName || "",
      description: data?.description || "",
      membershipRules:
        Array.isArray(data?.membershipRules) ?
          data.membershipRules.map((rule) => ({
            ...emptyRule(),
            ...rule,
            value: rule?.value ?? "",
            valueTo: rule?.valueTo ?? "",
          }))
        : [],
      membershipLogic: data?.membershipLogic || "ANY",
      reviewPeriodDays: data?.reviewPeriodDays ?? "",
      precedence: data?.precedence ?? 100,
      sequence: data?.sequence ?? 0,
    });
  }, [open, data]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const rules = form.membershipRules || [];
  const updateRule = (index, patch) =>
    set({
      membershipRules: rules.map((rule, i) =>
        i === index ? { ...rule, ...patch } : rule,
      ),
    });

  const validationError = useMemo(() => {
    const base =
      validateCode(form.code, { isUpdate, label: "Cohort code" }) ||
      validateRequired(form.cohortName, "Cohort name");
    if (base) return base;

    for (let i = 0; i < rules.length; i += 1) {
      const rule = rules[i];
      const position = `Rule ${i + 1}`;

      if (
        FIELD_BEARING_SOURCES.includes(rule.sourceType) &&
        !rule.field?.trim()
      ) {
        return `${position}: a field name is required for ${humanise(rule.sourceType)}`;
      }
      if (rule.operator === "EXISTS" && String(rule.value).trim() !== "") {
        return `${position}: the EXISTS operator does not take a value`;
      }
      if (rule.operator !== "EXISTS" && String(rule.value).trim() === "") {
        return `${position}: a value is required`;
      }
      if (rule.operator === "BETWEEN" && String(rule.valueTo).trim() === "") {
        return `${position}: BETWEEN requires an upper bound`;
      }
      if (rule.operator !== "BETWEEN" && String(rule.valueTo).trim() !== "") {
        return `${position}: an upper bound is only valid with BETWEEN`;
      }
    }

    return "";
  }, [form, rules, isUpdate]);

  const handleSubmit = async () => {
    if (validationError) return;
    setSaving(true);

    const payload = {
      cohortName: form.cohortName.trim(),
      description: form.description?.trim() || "",
      membershipRules: rules.map((rule) => ({
        sourceType: rule.sourceType,
        ...(rule.field?.trim() ? { field: rule.field.trim() } : {}),
        operator: rule.operator,
        ...(rule.operator === "EXISTS" ? {} : { value: rule.value }),
        ...(rule.operator === "BETWEEN" ? { valueTo: rule.valueTo } : {}),
        ...(rule.notes?.trim() ? { notes: rule.notes.trim() } : {}),
      })),
      membershipLogic: form.membershipLogic,
      reviewPeriodDays:
        form.reviewPeriodDays === "" ? null : Number(form.reviewPeriodDays),
      precedence: Number(form.precedence) || 0,
      sequence: Number(form.sequence) || 0,
    };

    try {
      if (isUpdate) {
        await riskCohortApi.update(cohortId, payload);
        toast.success("Risk cohort updated successfully");
      } else {
        await riskCohortApi.create({ ...payload, code: form.code.trim() });
        toast.success("Risk cohort created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} risk cohort`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <MasterModalShell
      open={open}
      onClose={onClose}
      isUpdate={isUpdate}
      entityLabel="Risk Cohort"
      saving={saving}
      validationError={validationError}
      onSubmit={handleSubmit}
      maxWidth="md"
    >
      <Box sx={{ display: "flex", gap: 2 }}>
        <CodeField
          value={form.code}
          onChange={(value) => set({ code: value })}
          isUpdate={isUpdate}
          label="Cohort Code"
          helperText='e.g. "diabetes" becomes DIABETES'
        />
        <TextField
          label="Cohort Name"
          required
          fullWidth
          size="small"
          value={form.cohortName || ""}
          onChange={(event) => set({ cohortName: event.target.value })}
        />
      </Box>

      <TextField
        label="Description"
        fullWidth
        size="small"
        multiline
        minRows={2}
        value={form.description || ""}
        onChange={(event) => set({ description: event.target.value })}
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          select
          label="Membership Logic"
          size="small"
          sx={{ minWidth: 160 }}
          value={form.membershipLogic || "ANY"}
          onChange={(event) => set({ membershipLogic: event.target.value })}
        >
          {COHORT_RULE_LOGIC.map((option) => (
            <MenuItem key={option} value={option}>
              {option === "ANY" ? "Any rule matches" : "All rules match"}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Review Period (days)"
          type="number"
          size="small"
          fullWidth
          value={form.reviewPeriodDays}
          onChange={(event) => set({ reviewPeriodDays: event.target.value })}
        />
        <TextField
          label="Precedence"
          type="number"
          size="small"
          sx={{ minWidth: 130 }}
          value={form.precedence}
          onChange={(event) => set({ precedence: event.target.value })}
          helperText="Lower wins"
        />
        <TextField
          label="Sequence"
          type="number"
          size="small"
          sx={{ minWidth: 120 }}
          value={form.sequence}
          onChange={(event) => set({ sequence: event.target.value })}
        />
      </Box>

      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Membership Rules
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1 }}
        >
          Stored as configuration for the future evaluation engine. Nothing
          evaluates them yet — cohort membership is asserted on the vaccination
          record itself.
        </Typography>

        {rules.map((rule, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            <TextField
              select
              size="small"
              label="Source"
              sx={{ minWidth: 170 }}
              value={rule.sourceType}
              onChange={(event) =>
                updateRule(index, { sourceType: event.target.value })
              }
            >
              {COHORT_RULE_SOURCES.map((source) => (
                <MenuItem key={source} value={source}>
                  {humanise(source)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Field"
              sx={{ minWidth: 140 }}
              required={FIELD_BEARING_SOURCES.includes(rule.sourceType)}
              value={rule.field || ""}
              onChange={(event) =>
                updateRule(index, { field: event.target.value })
              }
            />

            <TextField
              select
              size="small"
              label="Operator"
              sx={{ minWidth: 190 }}
              value={rule.operator}
              onChange={(event) =>
                updateRule(index, {
                  operator: event.target.value,
                  ...(event.target.value === "EXISTS" ? { value: "" } : {}),
                  ...(event.target.value !== "BETWEEN" ? { valueTo: "" } : {}),
                })
              }
            >
              {COHORT_RULE_OPERATORS.map((operator) => (
                <MenuItem key={operator} value={operator}>
                  {humanise(operator)}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Value"
              sx={{ minWidth: 130 }}
              disabled={rule.operator === "EXISTS"}
              value={rule.value ?? ""}
              onChange={(event) =>
                updateRule(index, { value: event.target.value })
              }
            />

            <TextField
              size="small"
              label="Upper bound"
              sx={{ minWidth: 130 }}
              disabled={rule.operator !== "BETWEEN"}
              value={rule.valueTo ?? ""}
              onChange={(event) =>
                updateRule(index, { valueTo: event.target.value })
              }
            />

            <IconButton
              onClick={() =>
                set({
                  membershipRules: rules.filter((_, i) => i !== index),
                })
              }
            >
              <DeleteOutlineIcon fontSize="small" color="error" />
            </IconButton>
          </Box>
        ))}

        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => set({ membershipRules: [...rules, emptyRule()] })}
        >
          Add rule
        </Button>
      </Box>
    </MasterModalShell>
  );
};

export default RiskCohortModal;
